// English source strings for the people & organisations stream (ADR-028, PD-370 – PD-389):
// organisation register and affiliations (PB-03-03), recorded person link review (PB-03-04), host
// reassignment (PB-03-02), group roster import (PB-04-03) and the collection matrix (PB-16-03).
export const enOrgs = {
  // ------------------------------------------------------------ validation and API messages
  'orgs.v.reason': 'Give a reason of at least 3 characters.',
  'orgs.v.reasonRequired': 'Give a reason for the name change; it is kept in the name history.',
  'orgs.v.item': 'Choose a known item.',
  'orgs.v.notCollectedHere': 'This information is not collected here. Identity documents and photographs are captured only through identity verification.',
  'orgs.v.disallowed': 'Not collected for this visitor type and purpose (data minimisation).',
  'orgs.v.nameTaken': 'An active organisation already uses this name.',
  'orgs.v.sponsorInactive': 'Choose an active host as sponsor.',
  'orgs.v.organisationUnknown': 'Choose an organisation from the register.',
  'orgs.v.organisationInactive': 'This organisation is deactivated.',
  'orgs.v.sameHost': 'Choose a different host from the current one.',
  'orgs.v.matrix.duplicate': 'This rule is listed twice for the same visitor type and purpose.',
  'orgs.v.matrix.unknown_item': 'Unknown item: it is not a built-in item, an active dictionary field or a pre-registration document.',
  'orgs.v.matrix.unknown_category': 'Unknown visitor type.',
  'orgs.v.matrix.purpose': 'Use a purpose code (lower-case letters, digits, - and _).',
  'orgs.v.matrix.capture_only': 'Photos and identity documents are captured only through identity verification; they cannot be required here.',
  'orgs.v.matrix.document_mode': 'Pre-registration documents can only be disallowed here; whether they are required stays in the visitor policy.',
  'orgs.v.matrix.too_many': 'Too many rules.',
  'orgs.api.contractorGoverned': 'This organisation is a contractor company: its name, type, status and sponsor are managed in Contractors & permits.',
  'orgs.api.contractorAffiliation': 'Contractor workers are affiliated in Contractors & permits, with their evidence.',
  'orgs.api.nameTakenOnReactivate': 'Another active organisation now uses this name. Rename one of them first.',
  'orgs.api.affiliationOverlap': 'This person already has an affiliation with this organisation for these dates.',
  'orgs.api.affiliationEnded': 'This affiliation already ends on or before that date.',
  'orgs.api.linkSamePerson': 'Choose two different profiles.',
  'orgs.api.linkAlreadyDecided': 'A decision for these two profiles is already recorded. Reverse it first.',
  'orgs.api.linkPrimaryIsLinked': 'This profile is linked to another primary profile. Record the link on that primary profile instead.',
  'orgs.api.linkDuplicateHasLinks': 'The other profile is the primary of linked profiles. Reverse those links first.',
  'orgs.api.linkReversed': 'This decision has already been reversed.',
  'orgs.api.reassignmentResolved': 'This visit no longer needs reassignment.',
  'orgs.api.rosterOpen': 'This file is already staged for this visit. Apply or discard that import first.',
  'orgs.api.rosterNotEditable': 'This roster import has been applied or discarded.',
  'orgs.api.rosterHasProblems': 'All-or-nothing needs every row valid and every possible duplicate decided.',
  'orgs.api.rosterRowFailed': 'A row could not be added, so nothing was added (all-or-nothing).',
  // ------------------------------------------------------------ collection items
  'orgs.item.email': 'Email address',
  'orgs.item.phone': 'Mobile number',
  'orgs.item.organisation': 'Organisation',
  'orgs.item.photo': 'Photograph',
  'orgs.item.id_document': 'Identity document image',
  'orgs.item.id_number': 'Identity document number',
  // ------------------------------------------------------------ navigation and roles
  'nav.people': 'People & organisations',
  'specialist.profile-steward': 'Profile steward',
  'pv.tab.collection': 'Collection matrix',
  'orgs.tab.organisations': 'Organisations',
  'orgs.tab.reassignments': 'Host reassignments',
  // ------------------------------------------------------------ organisation register (PB-03-03)
  'orgs.title': 'Organisations',
  'orgs.intro': 'Employers, contractor companies, vendors and delivery organisations. Records are renamed with history or deactivated, never deleted.',
  'orgs.register': 'Organisation register',
  'orgs.registerSub': 'Distinct from individual permissions: an organisation or affiliation grants no access.',
  'orgs.search': 'Search organisations',
  'orgs.anyKind': 'Any type',
  'orgs.allStatuses': 'All statuses',
  'orgs.none': 'No organisations match.',
  'orgs.add': 'Add organisation',
  'orgs.col.organisation': 'Organisation',
  'orgs.col.kind': 'Type',
  'orgs.col.status': 'Status',
  'orgs.kind.visitor': 'Visitor organisation',
  'orgs.kind.contractor': 'Contractor',
  'orgs.kind.vendor': 'Vendor',
  'orgs.kind.delivery': 'Delivery organisation',
  'orgs.kind.government': 'Government entity',
  'orgs.kind.other': 'Other',
  'orgs.governed': 'Managed in Contractors & permits',
  'orgs.governedNote': 'This organisation is a contractor company. Its name, type, status, sponsor and workers are managed in',
  'orgs.contractorHint': 'Contractor companies are added in Contractors & permits, with their evidence.',
  'orgs.displayName': 'Display name',
  'orgs.legalName': 'Legal name',
  'orgs.facts': 'Organisation facts',
  'orgs.sponsor': 'Sponsor',
  'orgs.created': 'Created',
  'orgs.edit': 'Edit',
  'orgs.renameHint': 'Required when the name changes; kept in the name history.',
  'orgs.deactivate': 'Deactivate',
  'orgs.reactivate': 'Reactivate',
  'orgs.deactivateHint': 'The organisation stays in the register with its history and visit attribution; it is no longer offered for new visits.',
  'orgs.reactivateHint': 'The organisation can be chosen again.',
  'orgs.contacts': 'Contacts',
  'orgs.noContacts': 'No contacts recorded.',
  'orgs.addContact': 'Add contact',
  'orgs.editContact': 'Edit contact',
  'orgs.contactName': 'Name',
  'orgs.primary': 'Primary contact',
  'orgs.nameHistory': 'Name history',
  'orgs.noRenames': 'Never renamed.',
  'orgs.byOtherModule': 'Contractor register',
  'orgs.attribution': 'Visits attributed to this organisation',
  'orgs.attributionSub': 'Each visit keeps the organisation name it was recorded with, also after a rename.',
  'orgs.attributedAs': 'Recorded as',
  'orgs.noVisits': 'No visits are attributed to this organisation in your scope.',
  'orgs.people': { one: 'One person', other: '{count} people' },
  'orgs.partial': 'Some records are outside your scope and are not listed.',
  // ------------------------------------------------------------ affiliations
  'orgs.aff.title': 'Affiliations',
  'orgs.aff.sub': 'Dated employment or association with organisations. Affiliations grant no access.',
  'orgs.aff.orgSub': 'People affiliated with this organisation that you can see.',
  'orgs.aff.none': 'No affiliations recorded.',
  'orgs.aff.add': 'Add affiliation',
  'orgs.aff.end': 'End affiliation',
  'orgs.aff.role': 'Role',
  'orgs.aff.period': 'Period',
  'orgs.aff.source': 'Source',
  'orgs.aff.source.register': 'Organisation register',
  'orgs.aff.source.contractor_register': 'Contractor register (read-only)',
  'orgs.aff.startsOn': 'Starts on',
  'orgs.aff.endsOn': 'Ends on',
  'orgs.aff.endsOnHint': 'Leave empty while it continues.',
  'orgs.aff.open': 'open',
  'orgs.aff.current': 'Current',
  'orgs.aff.past': 'Not current',
  'orgs.aff.contractorHint': 'Contractor workers are affiliated in Contractors & permits.',
  // ------------------------------------------------------------ link review (PB-03-04)
  'orgs.links.title': 'Possible matches',
  'orgs.links.sub': 'Profiles you can see that share identifiers with this one. Suggestions never merge identities automatically.',
  'orgs.links.notProof': 'Shared contact information is not proof of identity. Review source evidence before linking.',
  'orgs.links.none': 'No possible matches among the profiles you can see.',
  'orgs.links.col.profile': 'Profile',
  'orgs.links.col.reasons': 'Match reasons',
  'orgs.links.reason.email': 'Same email',
  'orgs.links.reason.phone': 'Same phone',
  'orgs.links.reason.name': 'Same name',
  'orgs.links.reason.on_this_visit': 'Already on this visit',
  'orgs.links.reason.employee_number': 'Same employee number',
  'orgs.links.linkedProfiles': { one: 'One linked profile', other: '{count} linked profiles' },
  'orgs.links.review': 'Record match review',
  'orgs.links.stewardOnly': 'A profile steward decides.',
  'orgs.links.reviewTitle': 'Record match review',
  'orgs.links.reasonsFound': 'Match reasons: {reasons}',
  'orgs.links.decisionLabel': 'Decision',
  'orgs.links.keepSeparate': 'Keep separate',
  'orgs.links.link': 'Link as the same person',
  'orgs.links.linkHint': 'This profile becomes the primary profile. The other profile, its visits and their organisation names stay as they are; the link can be reversed.',
  'orgs.links.keepHint': 'The two profiles are no longer suggested as a match.',
  'orgs.links.reasonHint': 'Say what evidence you reviewed.',
  'orgs.links.record': 'Record decision',
  'orgs.links.decisions': 'Recorded decisions',
  'orgs.links.decision.linked': 'Linked',
  'orgs.links.decision.kept_separate': 'Kept separate',
  'orgs.links.status.active': 'In force',
  'orgs.links.status.reversed': 'Reversed',
  'orgs.links.primaryOf': '{other} is linked to {primary}',
  'orgs.links.by': 'by {who}, {when}',
  'orgs.links.reversedBy': 'reversed by {who}, {when}',
  'orgs.links.reverse': 'Reverse',
  'orgs.links.reverseTitle': 'Reverse decision',
  'orgs.links.reverseHint': 'The decision stays in the history; nothing about either profile is changed.',
  // ------------------------------------------------------------ host reassignment (PB-03-02)
  'orgs.ra.pageTitle': 'Host reassignments',
  'orgs.ra.pageIntro': 'Future visits whose primary host is no longer active. Give each visit an active host.',
  'orgs.ra.reassign': 'Reassign',
  'orgs.ra.title': 'Reassign the primary host',
  'orgs.ra.hint': 'Choose an active host. Changing the primary host is a material change: approvals are requested again and visitors receive fresh invitation links.',
  'orgs.ra.search': 'Search hosts',
  'orgs.ra.newHost': 'New primary host',
  'orgs.ra.impactTitle': 'Approvals will be requested again.',
  'orgs.ra.impact': { one: 'One approval of this visit will be invalidated and requested again.', other: '{count} approvals of this visit will be invalidated and requested again.' },
  'orgs.ra.confirm': 'I have reviewed the impact',
  // ------------------------------------------------------------ roster import (PB-04-03)
  'orgs.roster.open': 'Import roster',
  'orgs.roster.title': 'Import a group roster',
  'orgs.roster.intro': 'Every row becomes an independent participation with its own approvals and status. Possible duplicates need your decision; nothing is merged.',
  'orgs.roster.file': 'Roster file',
  'orgs.roster.fileHint': 'CSV (UTF-8) or XLSX. Photographs, identity documents and passwords are never imported.',
  'orgs.roster.inspect': 'Read columns',
  'orgs.roster.mapping': { one: 'Map the columns (one row)', other: 'Map the columns ({count} rows)' },
  'orgs.roster.blocked': 'These columns can never be imported: {columns}',
  'orgs.roster.ignore': 'Do not import',
  'orgs.roster.required': 'required',
  'orgs.roster.dryRun': 'Check the rows',
  'orgs.roster.counts': '{rows} rows: {valid} valid, {invalid} with problems, {duplicate} possible duplicates ({undecided} undecided).',
  'orgs.roster.applied': 'Applied: {added} added, {failed} failed, {skipped} skipped.',
  'orgs.roster.report': 'Row report',
  'orgs.roster.row': 'Row',
  'orgs.roster.status': 'Result',
  'orgs.roster.detail': 'Details',
  'orgs.roster.addedPerson': 'Added to the visit',
  'orgs.roster.st.valid': 'Ready',
  'orgs.roster.st.invalid': 'Has problems',
  'orgs.roster.st.duplicate': 'Possible duplicate',
  'orgs.roster.st.added': 'Added',
  'orgs.roster.st.failed': 'Not added',
  'orgs.roster.st.skipped': 'Skipped',
  'orgs.roster.useExisting': 'Use this profile',
  'orgs.roster.createSeparate': 'Create a separate profile',
  'orgs.roster.skip': 'Skip',
  'orgs.roster.policy': 'How to apply',
  'orgs.roster.validOnly': 'Add the ready rows; report the rest',
  'orgs.roster.allOrNothing': 'All or nothing: add every row or none',
  'orgs.roster.apply': 'Apply roster',
  'orgs.roster.done': '{added} added, {failed} not added, {skipped} skipped.',
  // ------------------------------------------------------------ collection matrix (PB-16-03)
  'orgs.mode.required': 'Required',
  'orgs.mode.optional': 'Optional',
  'orgs.mode.disallowed': 'Not collected',
  'orgs.cm.title': 'Collection matrix',
  'orgs.cm.intro': 'What may be collected for each visitor type. Disallowed values are refused by the server in visit, pre-registration and kiosk forms.',
  'orgs.cm.published': 'Version {n} published {when}',
  'orgs.cm.nonePublished': 'No matrix published: every item follows the visitor policy and the field dictionary.',
  'orgs.cm.draft': 'draft version {n} open',
  'orgs.cm.category': 'Visitor type',
  'orgs.cm.allTypes': 'Every visitor type (and kiosk requests)',
  'orgs.cm.item': 'Item',
  'orgs.cm.effective': 'In force',
  'orgs.cm.rule': 'Rule',
  'orgs.cm.class': 'Retention class',
  'orgs.cm.kind.builtin': 'Visitor fact',
  'orgs.cm.kind.field': 'Dictionary field',
  'orgs.cm.kind.document': 'Pre-registration document',
  'orgs.cm.captureOnly': 'identity verification only',
  'orgs.cm.source.matrix': 'from the matrix',
  'orgs.cm.source.default': 'from the policy or dictionary',
  'orgs.cm.followDefault': 'Follow the policy and dictionary',
  'orgs.cm.purposeRules': 'Rules for a business purpose',
  'orgs.cm.howEnforced': 'The most specific rule applies: visitor type and purpose, visitor type, every type and purpose, every type. A disallowed fact is never a submission requirement.',
  'orgs.cm.saveDraft': 'Save draft',
  'orgs.cm.publish': 'Publish',
  'orgs.cm.discard': 'Discard draft',
  'orgs.cm.readOnly': 'Privacy administrators change the matrix.',
  'orgs.cm.formNote': 'Some contact details are not collected for this visitor type (data minimisation).',
} as const;
